import { DefaultLoader, LoaderContainer } from 'components/Loader';
import { PageHeadContainer, PageTitle } from 'components/PageHead';
import MenteeFormEdit from 'components/UserForms/MenteeForm/MenteeFormEdit';
import {
  Errors,
  initialErrors,
  State,
} from 'components/UserForms/MenteeForm/types';
import { FC, useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import MenteeFormType from 'types/MenteeForm';
import privateAPI from 'utils/privateAxiosInstance';

const Head: FC = () => {
  return (
    <PageHeadContainer>
      <PageTitle>
        <h1>Questionnaire de mentorat</h1>
      </PageTitle>
    </PageHeadContainer>
  );
};

const initialState: State = {
  jobSearch: null,
  careerPlan: null,
  studies: null,
  entrepreneurship: null,
  sharing: false,
  volunteerState: null,
  volunteerOrganism: null,
  expertises: [],
  validatedDiploma: null,
  completedFormation: null,
  formationMatchingProject: null,
  intendedFormation: null,
  formationState: null,
  intendedDiploma: null,
  consideration: null,
  hobby: null,
  heardAboutMentorship: null,
  heardAboutMentorshipText: null,
  expectations: null,
  sharingPhone: null,
  sharingEmail: null,
};

const EditFormMentee: FC = () => {
  const [state, setState] = useState<State>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const { data: menteeForm, error } = useSWR<MenteeFormType>('/mentee-form/');
  useEffect(() => {
    if (menteeForm) {
      setState({
        jobSearch: menteeForm.jobSearch!,
        careerPlan: menteeForm.careerPlan!,
        studies: menteeForm.studies!,
        entrepreneurship: menteeForm.entrepreneurship!,
        sharing: menteeForm.sharing!,
        volunteerState: menteeForm.volunteerState!,
        volunteerOrganism: menteeForm.volunteerOrganism || null,
        expertises: menteeForm.expertiseFields!.map(
          (expertiseField) => expertiseField.expertise,
        ),
        validatedDiploma: menteeForm.validatedDiploma!,
        completedFormation: menteeForm.completedFormation!,
        formationMatchingProject: menteeForm.formationMatchingProject!,
        intendedFormation: menteeForm.intendedFormation!,
        formationState: menteeForm.formationState!,
        intendedDiploma: menteeForm.intendedDiploma!,
        consideration: menteeForm.consideration!,
        hobby: menteeForm.hobby!,
        sharingPhone: menteeForm.sharingPhone!,
        heardAboutMentorship: menteeForm.heardAboutMentorship || null,
        heardAboutMentorshipText: menteeForm.heardAboutMentorshipText || null,
        expectations: menteeForm.expectations || null,
        sharingEmail: menteeForm.sharingEmail!,
      });
    }
  }, [menteeForm]);

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    // checking number of priorities
    let priorities = 0;
    if (state.jobSearch === 'PRIORITY') {
      priorities += 1;
    }
    if (state.careerPlan === 'PRIORITY') {
      priorities += 1;
    }
    if (state.studies === 'PRIORITY') {
      priorities += 1;
    }
    if (state.entrepreneurship === 'PRIORITY') {
      priorities += 1;
    }
    if (priorities > 2) {
      window.scrollTo({ top: 0 });
      setLoading(false);
      return setErrors({
        ...initialErrors,
        priorityLevels: true,
      });
    }

    if (!(state.sharingEmail || state.sharingPhone)) {
      setLoading(false);
      return setErrors({
        ...initialErrors,
        permissions: true,
      });
    }

    if (!state.volunteerOrganism?.trim()) {
      setLoading(false);
      window.scrollTo({ top: 0 });
      return setErrors({
        ...initialErrors,
        volunteerOrganism: true,
      });
    }
    
    privateAPI
      .post('/mentee-form/', state)
      .then(() => {
        mutate('/mentee-form/');
        setLoading(false);
        setSuccess(true);
        window.scrollTo({ top: 0 });
      })
      .catch((err) => {
        setLoading(false);
        window.scrollTo({ top: 0 });
        if (err?.response?.status === 400) {
          return setErrors({
            ...initialErrors,
            badRequest: true,
          });
        }
        setErrors({
          ...initialErrors,
          general: true,
        });
      });
  };

  return menteeForm || error ? (
    <>
      <Head />
      {}
      <MenteeFormEdit
        title="Questionnaire de mentorat"
        subtitle={[
          'Grâce à vos réponses, nous identifierons le mentor avec lequel vous serez mis en relation.',
          'Ce mentor aura répondu à un questionnaire similaire, ce qui nous permettra de plus facilement identifier les binômes à constituer.',
          'IMPORTANT : les binômes seront formés selon la complémentarité de vos profils plutôt que sur la proximité géographique.',
        ]}
        marginTop="0.375rem"
        {...{
          state,
          setState,
          errors,
          setErrors,
          handleSubmit,
          loading,
          success,
        }}
        invalid={menteeForm?.invalid}
      />
    </>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

export default EditFormMentee;
